import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import "./Toast.scss";

const ToastCtx = createContext(null);

const CLOSE_ANIM_MS = 260;

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function ToastProvider({ children, max = 3, defaultDuration = 2000 }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const remove = useCallback((id) => {
    setToasts((prev) => {
      const next = prev.map((t) => (t.id === id ? { ...t, closing: true } : t));
      return next;
    });

    const t = setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
      const tm = timersRef.current.get(id);
      if (tm) clearTimeout(tm);
      timersRef.current.delete(id);
    }, CLOSE_ANIM_MS);

    return () => clearTimeout(t);
  }, []);

  const push = useCallback(
    ({ text, tone = "default", duration, id: customId } = {}) => {
      const id = customId || uid();
      const ttl = typeof duration === "number" ? duration : defaultDuration;

      setToasts((prev) => {
        const base = prev.filter((x) => !x.closing);
        const next = [{ id, text: String(text || ""), tone, closing: false }, ...base];
        return next.slice(0, Math.max(1, Number(max) || 3));
      });

      if (timersRef.current.get(id)) {
        clearTimeout(timersRef.current.get(id));
        timersRef.current.delete(id);
      }

      if (ttl > 0) {
        const tm = setTimeout(() => remove(id), ttl);
        timersRef.current.set(id, tm);
      }

      return id;
    },
    [defaultDuration, max, remove]
  );

  const api = useMemo(
    () => ({
      push,
      remove,
      success: (text, duration) => push({ text, tone: "success", duration }),
      error: (text, duration) => push({ text, tone: "danger", duration }),
      info: (text, duration) => push({ text, tone: "default", duration }),
    }),
    [push, remove]
  );

  return (
    <ToastCtx.Provider value={api}>
      {children}

      <div className="toastRoot" aria-live="polite" aria-relevant="additions">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toastItem ${t.closing ? "is-closing" : ""} ${
              t.tone === "danger" ? "toastItem--danger" : t.tone === "success" ? "toastItem--success" : ""
            }`}
            role="status"
            onClick={() => remove(t.id)}
          >
            <div className="toastText">
              {t.text.split("\n")[0]}
              {t.text.includes("\n") && (
                <small className="toastSub">{t.text.split("\n").slice(1).join(" ")}</small>
              )}
            </div>
            <button className="toastClose" onClick={() => remove(t.id)} aria-label="Закрити">
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    return {
      push: () => null,
      remove: () => {},
      success: () => null,
      error: () => null,
      info: () => null,
    };
  }
  return ctx;
}

export default ToastProvider;